import React, { useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { WSImagePlayer, WowzaWebRTCPlayer, WebRTCPlayer } from "orion-components/CBComponents";
import { Typography } from "@mui/material";
import { Translate } from "orion-components/i18n";
import { useSelector } from "react-redux";
import VideoTopBar from "./components/VideoTopBar";
import VideoBottomBar from "./components/VideoBottomBar";
import AvigilonWebRTCPlayer from "../Video/WebRTCPlayer/Avigilon/AvigilonWebRTCPlayer";

import VicadsLiveWebRTCPlayer from "../Video/WebRTCPlayer/Vicads/VicadsLiveWebRTCPlayer";
import VicadsRecordedWebRTCPlayer from "../Video/WebRTCPlayer/Vicads/VicadsRecordedWebRTCPlayer";
import VideoIntegratedControls from "../../Profiles/Widgets/PTZControls/VideoIntegratedControls";
import FlashphonerRecordedWebRTCPlayer from "../Video/WebRTCPlayer/Flashphoner/FlashphonerRecordedWebRTCPlayer";
import VideoTranslatorPlayer from "../Video/WebRTCPlayer/VideoTranslator/Player";
import { fitAspectInsideContainer, useElementSize, getStreamURL, getPlaybackURL, usePrevious } from "./utils";
import { getCanControlPTZ } from "../../GlobalData/PTZPrivilege/selectors";

const propTypes = {
	camera: PropTypes.shape({
		id: PropTypes.string.isRequired,
		player: PropTypes.shape({
			type: PropTypes.string.isRequired,
			url: PropTypes.string,
			replayType: PropTypes.string
		}),
		entityData: PropTypes.object
	}).isRequired,
	inDock: PropTypes.bool,
	fillAvailable: PropTypes.bool,
	setCameraPriority: PropTypes.func,
	instanceId: PropTypes.string,
	entityId: PropTypes.string,
	entityType: PropTypes.string,
	readOnly: PropTypes.bool,
	playbackStartTime: PropTypes.oneOfType([PropTypes.date, PropTypes.object]),
	playBarValue: PropTypes.oneOfType([PropTypes.date, PropTypes.object]),
	playbackPlaying: PropTypes.bool,
	currentReplayMedia: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	addReplayMedia: PropTypes.func,
	removeReplayMedia: PropTypes.func,
	expanded: PropTypes.bool,
	isInFullScreen: PropTypes.bool,
	isOnAlertProfile: PropTypes.bool,
	isInVideoWall: PropTypes.bool,
	alertDateTime: PropTypes.any,
	removeFromWall: PropTypes.func,
	toggleFullScreenCameraDialog: PropTypes.func,
	isOnProfile: PropTypes.bool,
	defaultToRecorded: PropTypes.bool,
	initialRecordedDuration: PropTypes.string,
	initialRecordedTimeUnit: PropTypes.string
};

const defaultProps = {
	camera: { player: { url: null } },
	inDock: false,
	fillAvailable: false,
	setCameraPriority: () => {},
	instanceId: "",
	entityId: "",
	entityType: "",
	expanded: false,
	isOnAlertProfile: false,
	isInVideoWall: false,
	isOnProfile: false,
	alertDateTime: "",
	defaultToRecorded: false
};

const styles = {
	frame: {
		position: "relative",
		backgroundColor: "#000",
		width: "100%",
		height: "100%",
		maxWidth: "100%",
		maxHeight: "100%",
		overflow: "hidden"
	}
};

const LiveVideoPlayer = props => {
	const {
		camera,
		instanceId,
		entityId,
		entityType,
		readOnly,
		playbackStartTime,
		playBarValue,
		playbackPlaying,
		currentReplayMedia,
		addReplayMedia,
		removeReplayMedia,
		inDock,
		expanded,
		isInFullScreen,
		isOnAlertProfile,
		isInVideoWall,
		removeFromWall,
		toggleFullScreenCameraDialog,
		isOnProfile,
		alertDateTime,
		defaultToRecorded,
		playbackStartOffset,
		initialRecordedDuration,
		initialRecordedTimeUnit,
		clusterProps,
		hideRemoveFromWall
	} = props;
	const [initDimensionsDetected, setInitDimensionsDetected] = useState(false);
	const [streamUrl, setStreamUrl] = useState(null);
	const [streamUrlError, setStreamUrlError] = useState(false);
	const [selectedVideoMode, setSelectedVideoMode] = useState("Live");
	const [playbackStartDateTime, setPlaybackStartDateTime] = useState("");
	const [playbackEndDateTime, setPlaybackEndDateTime] = useState("");
	const [playbackStreamName, setPlaybackStreamName] = useState("");
	const [playbackUrl, setPlaybackUrl] = useState(null);
	const [playbackUrlError, setPlaybackUrlError] = useState(false);
	const [resizeTo, setResizeTo] = useState("dock");
	const [videoPlayerHovered, setVideoPlayerHovered] = useState(false);

	const ptzLockEnabled = useSelector(state => getCanControlPTZ(state, { cameraId: camera.id }));

	// Callback function to update startTime and endTime in parent
	const handleSetStartAndEndTime = (newStartTime, newEndTime) => {
		setPlaybackStartDateTime(newStartTime);
		setPlaybackEndDateTime(newEndTime);
	};

	// eslint-disable-next-line no-unused-vars
	const [_, forceUpdate] = useState(0); // Destructure state to discard actual value
	const [dockContainerDims, setDockContainerDims] = useState({ width: "", height: "" });

	const frame = useRef(null);
	const frameStartHeight = useRef(null);
	const { bccContainerRef, size, updateDimensions } = useElementSize();

	const dockData = useSelector(state => state.appState?.dock?.dockData);
	const cameraDock = useSelector(state => state.appState?.dock?.cameraDock);

	const iscameraInDock =
		dockData.isOpen && dockData.tab === "Cameras" && cameraDock.dockedCameras.includes(camera.id);

	const docked = iscameraInDock && isOnProfile ? true : false;

	const prevProps = usePrevious(props);

	useEffect(() => {
		if (defaultToRecorded) {
			setSelectedVideoMode("recorded");
		} else {
			setSelectedVideoMode("Live");
		}
	}, [defaultToRecorded]);

	useEffect(() => {
		const fetchStreamUrl = async () => {
			if (
				camera.player?.type !== "ws-jpeg" &&
				camera.player?.type !== "wowza" &&
				camera.player?.type !== "avigilon" &&
				camera.player?.type !== "vicads"
			) {
				const streamUrl = await getStreamURL(camera.id); // Await the promise
				if (streamUrl !== null) {
					setStreamUrl(streamUrl);
				} else {
					setStreamUrlError(true);
				}
			}
		};

		fetchStreamUrl();
	}, [camera]);

	useEffect(() => {
		if (prevProps) {
			const { id, player } = camera;
			const prevId = prevProps.camera ? prevProps.camera.id : null;

			if (
				id !== prevId &&
				(player?.type !== "wowza" || player?.type !== "vicads" || player?.type !== "avigilon")
			) {
				const fetchStreamUrl = async () => {
					const streamUrl = await getStreamURL(camera.id); // Await the promise
					if (streamUrl !== null) {
						setStreamUrl(streamUrl);
					} else {
						setStreamUrlError(true);
					}
				};

				fetchStreamUrl();
			}
		}
	}, [props]);

	useEffect(() => {
		if (selectedVideoMode !== "Live") {
			const fetchPlaybackUrl = async () => {
				const start = playbackStartDateTime;
				const end = playbackEndDateTime;
				if (start && end) {
					const playbackUrl = await getPlaybackURL(camera.id, start, end);
					if (playbackUrl !== null) {
						setPlaybackUrl(playbackUrl);
					} else {
						setPlaybackUrlError(true);
					}
				}
			};

			fetchPlaybackUrl();
		}
	}, [selectedVideoMode, camera, playbackStartDateTime, playbackEndDateTime, camera.id]);

	useEffect(() => {
		if (selectedVideoMode !== "recorded") {
			setPlaybackEndDateTime(null);
			setPlaybackStartDateTime(null);
		}
	}, [selectedVideoMode]);

	const getPlaybackStreamName = useCallback(streamName => {
		setPlaybackStreamName(streamName);
	}, []);

	const hasFeature = feature => {
		return camera.entityData.properties.features && camera.entityData.properties.features.includes(feature);
	};

	const getPlayer = () => {
		const { player, id } = camera;
		// -- handle cameras that have different player types for live and replay streaming
		const type =
			(readOnly || selectedVideoMode.toLowerCase() === "recorded") && player?.replayType
				? player?.replayType
				: player?.type;
		switch (type) {
			case "native":
				return player ? (
					<VideoTranslatorPlayer
						instanceId={instanceId}
						camera={camera}
						player={player}
						streamUrl={streamUrl}
						selectedVideoMode={selectedVideoMode.toLowerCase()}
					/>
				) : null;
			case "wowza":
				return (
					<WowzaWebRTCPlayer
						camera={camera}
						isForPlayback={selectedVideoMode === "recorded" ? true : false}
						start={playbackStartDateTime}
						end={playbackEndDateTime}
						getPlaybackStreamName={getPlaybackStreamName}
						isOnAlertProfile={isOnAlertProfile}
						contextId={entityId}
					/>
				);
			case "flashphoner": {
				if (selectedVideoMode === "Live") {
					return <WebRTCPlayer url={player?.url} streamUrl={streamUrl} streamUrlError={streamUrlError} />;
				} else {
					return (
						<FlashphonerRecordedWebRTCPlayer
							url={player?.url}
							playbackUrl={playbackUrl}
							playbackUrlError={playbackUrlError}
							onSetStartAndEndTime={handleSetStartAndEndTime}
						/>
					);
				}
			}

			case "ws-jpeg":
				return (
					<WSImagePlayer
						cameraId={id}
						videoProfile="desktop"
						resizeTo={resizeTo}
						instanceId={instanceId}
						entityId={entityId}
						entityType={entityType}
						playbackStartTime={readOnly && playbackStartTime ? playbackStartTime : null}
						playBarValue={readOnly && playBarValue ? playBarValue : null}
						playbackPlaying={readOnly && playbackPlaying ? playbackPlaying : null}
						currentReplayMedia={readOnly && currentReplayMedia ? currentReplayMedia : []}
						addReplayMedia={readOnly && addReplayMedia ? addReplayMedia : () => {}}
						removeReplayMedia={readOnly && removeReplayMedia ? removeReplayMedia : () => {}}
						selectedVideoMode={selectedVideoMode}
						api={camera.player.api || "liveStream"}
					/>
				);
			case "file":
				return (
					<video muted autoPlay loop style={{ maxHeight: "100%", maxWidth: "100%" }}>
						<source
							src={camera.entityData.properties.videoUrl}
							type={camera.entityData.properties.videoType}
						/>
					</video>
				);
			case "vicads": {
				if (selectedVideoMode === "Live") {
					return (
						<VicadsLiveWebRTCPlayer
							camera={camera}
							instanceId={instanceId}
							updateDimensions={updateDimensions}
							isInFullScreen={isInFullScreen}
							containerRef={frame}
						/>
					);
				} else {
					return (
						<VicadsRecordedWebRTCPlayer
							camera={camera}
							instanceId={instanceId}
							containerRef={frame}
							isInFullScreen={isInFullScreen}
							alertDateTime={alertDateTime}
							updateDimensions={updateDimensions}
						/>
					);
				}
			}
			case "avigilon": {
				return (
					<AvigilonWebRTCPlayer
						camera={camera}
						instanceId={instanceId}
						updateDimensions={updateDimensions}
						isInFullScreen={isInFullScreen}
						containerRef={frame}
						selectedVideoMode={selectedVideoMode}
					/>
				);
			}

			default:
				return null;
		}
	};

	useEffect(() => {
		const calculateDimensions = () => {
			if (calculateAspectDimensions()) {
				setInitDimensionsDetected(true);
			} else {
				// Dimensions could not be determined yet, we try again after 200 ms.
				setTimeout(calculateDimensions, 200);
			}
		};

		calculateDimensions();
	}, []);

	useEffect(() => {
		calculateAspectDimensions(isInFullScreen);
	}, [size, isInFullScreen]);

	const calculateAspectDimensions = (isVideoInFullScreen = false) => {
		// full-screen aspect ratio
		if (isVideoInFullScreen) {
			const containerHeight = window.innerHeight;
			const containerWidth = window.innerWidth;
			const aspectRatio = 16 / 9; // 16:9 aspect ratio
			const aspectHeight = containerWidth / aspectRatio;
			if (containerHeight < aspectHeight) {
				const newAspectWidth = containerHeight * aspectRatio;
				frame.current.style.width = `${newAspectWidth}px`;
			} else {
				frame.current.style.width = `${window.innerWidth}px`;
			}

			frame.current.style.height = `${window.innerHeight}px`;

			setResizeTo("fullscreen");
		} else {
			if (frameStartHeight.current) {
				frame.current.style.height = `${frameStartHeight.current}px`;
			} else {
				frameStartHeight.current = frame.current?.clientHeight;
			}
			const container = bccContainerRef.current;
			if (container?.clientWidth) {
				if (inDock && dockContainerDims.width !== "" && dockContainerDims.height !== "") {
					frame.current.style.width = dockContainerDims.width;
					frame.current.style.height = dockContainerDims.height;
				} else {
					const { width, height } = fitAspectInsideContainer(container, 16, 9);
					frame.current.style.width = `${width}px`;
					frame.current.style.height = `${height}px`;
				}
				const frameHeight = frame.current.style.height.replace(/px$/, "");
				if (inDock && Number(frameHeight) < 220) {
					setDockContainerDims({ width: frame.current.style.width, height: frame.current.style.height });
				}
				setResizeTo("dock");
			} else {
				return false;
			}
		}

		// Set height and width of nested video tag with class 'video-element' to match frame
		// const videoEl = frame.current?.querySelector(".video-element");
		// if (videoEl) {
		// 	videoEl.style.width = frame.current.style.width;
		// 	videoEl.style.height = frame.current.style.height;
		// }

		forceUpdate(prevCount => prevCount + 1);
		return true;
	};

	const getSelectedVideoMode = videoMode => {
		setSelectedVideoMode(videoMode);
	};

	const drawAspectRatio = () => {
		const contents = docked ? (
			<Typography style={{ position: "absolute" }} variant="subtitle1">
				{docked ? (
					<Translate value="global.CBComponents.video.videoPlayer.camInDock" />
				) : (
					<Translate value="global.CBComponents.video.videoPlayer.camInModal" />
				)}
			</Typography>
		) : (
			getPlayer()
		);

		return (
			<div
				className="bccVideoPlayerContainer"
				style={{ height: "100%", width: "100%", zIndex: dockData.isOpen ? 500 : 1000 }}
				ref={bccContainerRef}
				onMouseEnter={() => setVideoPlayerHovered(true)}
				onMouseLeave={() => setVideoPlayerHovered(false)}
			>
				<VideoTopBar
					camera={camera}
					readOnly={readOnly}
					getSelectedVideoMode={getSelectedVideoMode}
					isInFullScreen={isInFullScreen}
					expanded={expanded}
					inDock={inDock}
					isInVideoWall={isInVideoWall}
					removeFromWall={removeFromWall}
					toggleFullScreenCameraDialog={toggleFullScreenCameraDialog}
					currentVideoMode={selectedVideoMode}
					clusterProps={clusterProps}
					isOnAlertProfile={isOnAlertProfile}
					hideRemoveFromWall={hideRemoveFromWall}
					videoPlayerHovered={videoPlayerHovered}
					instanceId={instanceId}
				/>
				<div
					style={{
						width: "100%",
						height: "100%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center"
					}}
					className="center"
				>
					<div ref={frame} style={styles.frame}>
						<div className="bcc-maintain-height-aspect-ratio-box">
							{initDimensionsDetected &&
							hasFeature("control") &&
							hasFeature("video-integrated-control") &&
							ptzLockEnabled ? (
								<VideoIntegratedControls
									camera={camera}
									instanceId={instanceId}
									customStyles={inDock ? { top: 0, left: 0 } : {}}
								/>
							) : (
								<></>
							)}
							{initDimensionsDetected && <div className="bcc-aspect-ratio-box-inside">{contents}</div>}
						</div>
					</div>
				</div>
				{initDimensionsDetected && (
					<VideoBottomBar
						selectedVideoMode={selectedVideoMode}
						camera={camera}
						contextId={entityId}
						playbackStreamName={playbackStreamName}
						isOnAlertProfile={isOnAlertProfile}
						instanceId={instanceId}
						inDock={inDock}
						alertDateTime={alertDateTime}
						hasControlFeature={hasFeature("control")}
						defaultToRecorded={defaultToRecorded}
						playbackStartOffset={playbackStartOffset}
						initialRecordedDuration={initialRecordedDuration}
						initialRecordedTimeUnit={initialRecordedTimeUnit}
					/>
				)}
			</div>
		);
	};

	return <>{drawAspectRatio()}</>;
};

LiveVideoPlayer.propTypes = propTypes;
LiveVideoPlayer.defaultProps = defaultProps;

export default LiveVideoPlayer;
